import { jsx } from 'react/jsx-runtime';
import { safeRest, defaultFonts, visuallyHidden } from '@wds/utils';
import { IconX } from '@wds/icons';
import { createMemoStyles } from '@wds/styles';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise, SuppressedError, Symbol, Iterator */


var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

var useStyles$1 = createMemoStyles(function (theme) {
  var _a;
  var spacing = theme.spacing,
    type = theme.type,
    breakpoints = theme.breakpoints;
  return {
    wrenchCloseButton: (_a = {
      fontSize: type.fontSize.body,
      display: 'inline-flex',
      justifyContent: 'center',
      alignItems: 'center',
      cursor: function (_a) {
        var disabled = _a.disabled;
        return !disabled && 'pointer';
      },
      borderRadius: '0',
      textDecoration: 'none',
      background: 'transparent',
      border: 'none',
      position: function (_a) {
        var component = _a.component;
        if (component === 'alert' || component === 'banner') return null;else if (component === 'badge') return 'relative';else return 'absolute';
      },
      top: function (_a) {
        var component = _a.component;
        if (component === 'alert' || component === 'banner') return null;else if (component === 'badge') return 'none';else if (component === 'bannerCondensed') return spacing[4];else return spacing[1];
      },
      right: function (_a) {
        var component = _a.component;
        if (component === 'alert' || component === 'banner') return null;else if (component === 'badge') return 'none';else if (component === 'bannerCondensed') return spacing[4];else return spacing[1];
      },
      padding: function (_a) {
        var component = _a.component;
        if (component === 'alert') return [[spacing[2], 0, spacing[2], spacing[5]]];else if (component === 'badge') return 0;else if (component === 'banner') return [[0, spacing[1], 0, spacing[1]]];else if (component === 'bannerCondensed') return [[0, spacing[2], 0, spacing[1]]];else return spacing[2];
      },
      marginLeft: function (_a) {
        var component = _a.component;
        return component === 'badge' ? spacing[3] : null;
      },
      textAlign: function (_a) {
        var component = _a.component;
        return component === 'alert' ? 'right' : null;
      },
      '&:hover': {
        transition: 'none',
        textDecoration: 'none'
      },
      '&:active': {
        transition: 'none',
        outline: 'none'
      }
    }, _a["@media (max-width: ".concat(breakpoints.md, "px)")] = {
      position: function (_a) {
        var component = _a.component;
        return component === 'banner' && 'absolute';
      },
      top: function (_a) {
        var component = _a.component;
        return component === 'banner' && spacing[4];
      },
      right: function (_a) {
        var component = _a.component;
        return component === 'banner' && spacing[4];
      },
      padding: function (_a) {
        var component = _a.component;
        return component === 'banner' && [[0, spacing[2], 0, spacing[1]]];
      }
    }, _a)
  };
});

var CloseButton = function CloseButton(_a) {
  _a.children;
    var component = _a.component,
    label = _a.label,
    _c = _a.variant,
    variant = _c === void 0 ? 'light' : _c,
    _d = _a.disabled,
    disabled = _d === void 0 ? false : _d,
    rest = __rest(_a, ["children", "component", "label", "variant", "disabled"]);
  var wrenchCloseButton = useStyles$1({
    component: component,
    disabled: disabled
  }).wrenchCloseButton;
  return jsx("button", __assign({
    type: "button",
    className: wrenchCloseButton,
    "aria-label": label,
    disabled: disabled
  }, safeRest(rest), {
    children: jsx(IconX, {
      title: "close",
      color: variant
    })
  }));
};

var Overlay = function Overlay(_a) {
  var className = _a.className,
    onClick = _a.onClick;
  return jsx("div", {
    className: className,
    onClick: onClick,
    "data-testid": "Overlay__WDS"
  });
};

var useStyles = createMemoStyles(function (_a) {
  var palette = _a.palette,
    spacing = _a.spacing,
    theme = __rest(_a, ["palette", "spacing"]);
  var pane = {
    bgColor: palette.background.accent,
    padding: spacing[5]
  };
  return {
    wrenchPane: function (_a) {
      var transparent = _a.transparent;
      return __assign(__assign({}, defaultFonts(theme)), {
        background: transparent ? 'none' : pane.bgColor
      });
    },
    wrenchPaneHidden: function () {
      return __assign({}, visuallyHidden);
    },
    wrenchPaneBody: function () {
      return {
        padding: pane.padding
      };
    }
  };
});

var Pane = function Pane(_a) {
  var children = _a.children,
    _b = _a.hide,
    hide = _b === void 0 ? false : _b,
    id = _a.id,
    _c = _a.padding,
    padding = _c === void 0 ? false : _c,
    labelledBy = _a.labelledBy,
    role = _a.role,
    _d = _a.transparent,
    transparent = _d === void 0 ? false : _d,
    rest = __rest(_a, ["children", "hide", "id", "padding", "labelledBy", "role", "transparent"]);
  var _e = useStyles({
      hide: hide,
      transparent: transparent
    }),
    wrenchPane = _e.wrenchPane,
    wrenchPaneHidden = _e.wrenchPaneHidden,
    wrenchPaneBody = _e.wrenchPaneBody;
  return jsx("div", __assign({
    className: hide ? wrenchPaneHidden : wrenchPane,
    role: role,
    id: id,
    "aria-labelledby": labelledBy,
    "aria-hidden": hide
  }, safeRest(rest), {
    children: padding ? jsx("div", {
      className: wrenchPaneBody,
      children: children
    }) : children
  }));
};

export { CloseButton, Overlay, Pane };
